import { Input as InputPrimitive } from "@base-ui/react/input";
import * as React from "react";

import { cn } from "../../utils/cn";

import { forcedFieldBorderClasses } from "./force-state";
import { textVariants } from "./text";

/**
 * Form input primitive — single-line `<input>` with the canonical
 * Solomon palette baked in. Two visual recipes:
 *
 *   surface="transparent" + size="sm" (default) → inline panel field
 *     (`h-8 bg-transparent px-2.5 py-1`) — used by OnboardingPanel
 *     treasury form, where the field blends into the surrounding card.
 *
 *   surface="filled" + size="md" → modal / settings field
 *     (`h-10 bg-surface-sunken px-3`) — taller, filled treatment for
 *     focal inputs in dialogs and settings sections.
 *
 * The two recipes share everything else (semantic palette, focus ring,
 * disabled handling, aria-invalid). Pick the recipe by context, not
 * personal taste — inline panel forms are `transparent/sm`; modal +
 * top-level settings are `filled/md`.
 */
export type InputSurface = "transparent" | "filled";
export type InputSize = "sm" | "md";
/** Status state for inputs that need a visual cue beyond default/focus/disabled.
 *  Per Figma `Solomon-App-v3.0` node 2157:9111 — borders shift to the matching
 *  status-X-500 token. `error` additionally sets `aria-invalid` for a11y. */
export type InputState = "success" | "warning" | "error";

interface InputProps extends Omit<React.ComponentProps<"input">, "size"> {
    surface?: InputSurface;
    /** Visual size of the input shell — `sm` (h-8, default) or `md` (h-10).
     *  Replaces the HTML `size` attribute (which sets visible character width
     *  and is rarely used in modern UI). If you need the native HTML behavior,
     *  pass it via the underlying props after this primitive is dropped. */
    size?: InputSize;
    /** Status state — shifts the border to the matching status color.
     *  `error` also sets `aria-invalid` so screen readers + native validation
     *  styling pick it up. */
    state?: InputState;
    /** Switch the embedded typography from `body` (ABC Social, default) to
     *  `number` (APK Narrative, tabular-nums). Use for amount/quantity/percent
     *  inputs where the user is entering financial figures. */
    numeric?: boolean;
    /** Demo affordance — force the hover VISUAL (darker border) on with no
     *  pointer present. CSS `:hover` can't be JS-triggered, so scripted sims
     *  (recorded "user sessions") use this to paint the hover state under a
     *  simulated cursor. Emits the same border utility the real `hover:`
     *  curve produces, unconditionally. A `state` prop still wins (it trails
     *  the cascade). Default `false` — existing callers unaffected. */
    forceHover?: boolean;
    /** Demo affordance — force the focus VISUAL (focus border) on with no
     *  pointer/keyboard focus present. Emits the same border utility the real
     *  `focus-visible:` curve produces. Wins over `forceHover` (focus reads
     *  as the more specific state). Default `false`. */
    forceFocus?: boolean;
}

const SURFACE_CLASSES: Record<InputSurface, string> = {
    transparent: "bg-transparent",
    filled: "bg-surface-sunken"
};

const SIZE_CLASSES: Record<InputSize, string> = {
    sm: "h-8 px-2.5 py-1",
    md: "h-10 px-3"
};

const STATE_BORDER: Record<InputState, string> = {
    success: "border-status-success-500",
    warning: "border-status-warning-500",
    error: "border-status-error-500"
};

function Input({
    className,
    type,
    surface = "transparent",
    size = "sm",
    state,
    numeric = false,
    forceHover = false,
    forceFocus = false,
    ...props
}: InputProps): React.ReactElement {
    return (
        <InputPrimitive
            type={type}
            data-slot="input"
            {...(state === "error" ? { "aria-invalid": true } : {})}
            className={cn(
                textVariants({ variant: numeric ? "number" : "body" }),
                numeric && "tabular-nums",
                SIZE_CLASSES[size],
                SURFACE_CLASSES[surface],
                "w-full min-w-0 rounded-none border",
                // Border-state cascade — borders communicate state, not rings
                // (per Figma `Solomon-App-v3.0` node 2157:9111). Default →
                // hover → focus all live on this primitive; the status states
                // (success/warning/error) come from the `state` prop and win
                // over the interaction states because they trail the cascade.
                "border-line-base hover:border-charcoal-700",
                "focus-visible:outline-none focus-visible:border-charcoal-500",
                "text-foreground-base transition-colors",
                // file: pseudo-element styles target <input type="file">'s
                // ::file-selector-button (browser-built UI). Tailwind's `file:`
                // prefix can't compose with textVariants()'s static class
                // string — JIT can't detect runtime-prefixed classes. Raw
                // file:text-base + file:font-medium are kept intentionally;
                // not typography drift. No production callers use type="file"
                // currently, so this is showcase/defensive styling only.
                "file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-base file:font-medium file:text-foreground-base",
                // Placeholder uses `foreground-imprint` (the dimmest text
                // tone) and clears on focus so it's never mistaken for a
                // pre-filled default value once the user has clicked in.
                "placeholder:text-foreground-imprint focus:placeholder:text-transparent",
                "disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-line-neutral disabled:text-foreground-imprint",
                // Forced demo hover/focus (sim): literal border tokens so the
                // border darkens without a pointer. Trails the base border so
                // tailwind-merge resolves in its favor; still precedes `state`
                // so a status border wins.
                forcedFieldBorderClasses(forceHover, forceFocus),
                // Status states win over interaction states via prop-driven
                // className order (cn's tailwind-merge collapses the cascade).
                state && STATE_BORDER[state],
                className
            )}
            {...props}
        />
    );
}

export { Input };
